import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Search } from 'lucide-react';

export interface DropdownOption<T extends string = string> {
  value: T;
  label: string;
  /** Optional secondary text shown to the right / below the label */
  badge?: string;
  badgeClass?: string;
  /** Optional description shown below the label */
  description?: string;
}

interface CustomDropdownProps<T extends string = string> {
  options: DropdownOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Placeholder when no value selected */
  placeholder?: string;
  /** Minimum width of the trigger button */
  minWidth?: string;
  /** id passed to the underlying button for label association */
  id?: string;
  /** Full-width mode (100% width) — default true */
  fullWidth?: boolean;
  /** Force menu to open upward instead of downward */
  openUpward?: boolean;
}

export function CustomDropdown<T extends string = string>({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  minWidth,
  id,
  fullWidth = true,
  openUpward = false,
}: CustomDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = options.find(o => o.value === value);

  // Auto-detect if there isn't enough room below and flip upward
  useEffect(() => {
    if (!open || openUpward) return;
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    // Estimate menu height: ~40px per item, capped at max-h-64 (256px)
    const menuHeight = Math.min(options.length * 40, 256);
    setFlipped(spaceBelow < menuHeight + 8 && rect.top > spaceBelow);
  }, [open, openUpward, options.length]);

  // Close on outside click (backup for overlay)
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const shouldOpenUpward = openUpward || flipped;

  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`} ref={containerRef}>
      <button
        type="button"
        id={id}
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 h-11 px-3 rounded-lg border border-stone bg-white
                   text-small font-medium text-midnight hover:border-plum/40
                   transition-colors ${fullWidth ? 'w-full' : ''}`}
        style={minWidth ? { minWidth } : undefined}
      >
        <span className={`flex-1 text-left truncate ${!selected ? 'text-slate italic' : ''}`}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-slate transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <ul className={`absolute left-0 z-50 w-full min-w-[180px] bg-white border border-stone
                         rounded-xl shadow-brand-md overflow-hidden max-h-64 overflow-y-auto
                         ${shouldOpenUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
            {options.map((opt, i) => (
              <li key={opt.value}>
                <button
                  type="button"
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                  className={`w-full text-left px-3 py-2.5 text-[13px] hover:bg-mist transition-colors
                    ${i > 0 ? 'border-t border-stone' : ''}
                    ${value === opt.value ? 'font-semibold text-plum bg-plum/5' : 'text-midnight'}`}
                >
                  <span>{opt.label}</span>
                  {opt.badge && (
                    <span className={`ml-2 text-[11px] font-medium ${opt.badgeClass ?? 'text-slate'}`}>
                      {opt.badge}
                    </span>
                  )}
                  {opt.description && (
                    <span className="block text-[11px] text-slate font-normal leading-snug mt-0.5">
                      {opt.description}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ── Searchable Dropdown ────────────────────────────────────────────────────

interface SearchableDropdownProps<T extends string = string> {
  options: DropdownOption<T>[];
  value: T;
  onChange: (value: T) => void;
  placeholder?: string;
  id?: string;
  fullWidth?: boolean;
}

export function SearchableDropdown<T extends string = string>({
  options,
  value,
  onChange,
  placeholder = 'Search…',
  id,
  fullWidth = true,
}: SearchableDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const selected = options.find(o => o.value === value);

  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const openDropdown = useCallback(() => {
    setOpen(true);
    setQuery('');
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`} ref={containerRef}>
      <button
        type="button"
        id={id}
        onClick={openDropdown}
        className={`flex items-center gap-1.5 h-11 px-3 rounded-lg border border-stone bg-white
                   text-small font-medium text-midnight hover:border-plum/40
                   focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                   transition-colors ${fullWidth ? 'w-full' : ''}`}
      >
        <span className={`flex-1 text-left truncate ${!selected ? 'text-slate italic' : ''}`}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-slate transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-50 w-full min-w-[180px] bg-white border border-stone
                          rounded-xl shadow-brand-md overflow-hidden flex flex-col max-h-72">
            {/* Search input */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-stone">
              <Search size={14} className="shrink-0 text-slate" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Type to search…"
                className="flex-1 text-[13px] text-midnight placeholder:text-slate
                           bg-transparent outline-none"
              />
            </div>
            <ul ref={listRef} className="overflow-y-auto max-h-56">
              {filtered.length === 0 && (
                <li className="px-3 py-3 text-[13px] text-slate">No matches</li>
              )}
              {filtered.map((opt, i) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => { onChange(opt.value); setOpen(false); }}
                    className={`w-full text-left px-3 py-2.5 text-[13px] hover:bg-mist transition-colors
                      ${i > 0 ? 'border-t border-stone/50' : ''}
                      ${value === opt.value ? 'font-semibold text-plum bg-plum/5' : 'text-midnight'}`}
                  >
                    <span>{opt.label}</span>
                    {opt.badge && (
                      <span className={`ml-2 text-[11px] font-medium ${opt.badgeClass ?? 'text-slate'}`}>
                        {opt.badge}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
